import React, { useState, useMemo } from "react";
import { X, Search, Flame, Phone, ArrowUpDown } from "lucide-react";
import Staff from "../../../components/Staff";


const STAGE_CONFIG = {
  lead: { labelKey: "pipeline.lead", fallback: "Lead", color: "#64748b", bg: "#f1f5f9" },
  "add-zalo": { labelKey: "pipeline.addZalo", fallback: "Add Zalo", color: "#3b82f6", bg: "#eff6ff" },
  "book-test": { labelKey: "pipeline.bookTest", fallback: "Book Test", color: "#f59e0b", bg: "#fffbeb" },
  test: { labelKey: "pipeline.test", fallback: "Test", color: "#f97316", bg: "#fff7ed" },
  enrol: { labelKey: "pipeline.enrol", fallback: "Enrol", color: "#10b981", bg: "#ecfdf5" },
};

const HEAT_COLORS = {
  1: "#ef4444",
  2: "#f97316",
  3: "#eab308",
  4: "#22c55e",
  5: "#10b981",
};

export default function MarketingAllLeadsModal({
  open,
  onClose,
  contacts,
  stats,
  t,
  formatPhone,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const filteredContacts = useMemo(() => {
    let list = [...(contacts || [])];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (c) =>
          (c.phone || "").includes(q) ||
          (c.name || "").toLowerCase().includes(q) ||
          (c.location || "").toLowerCase().includes(q) ||
          (c.notes || "").toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case "createdAt":
          valA = new Date(a.createdAt || a.localDate || 0).getTime();
          valB = new Date(b.createdAt || b.localDate || 0).getTime();
          break;
        case "name":
          valA = (a.name || "").toLowerCase();
          valB = (b.name || "").toLowerCase();
          break;
        case "heat":
          valA = a.heat || 0;
          valB = b.heat || 0;
          break;
        case "lastContacted":
          valA = new Date(a.lastContacted || 0).getTime();
          valB = new Date(b.lastContacted || 0).getTime();
          break;
        default:
          valA = new Date(a.createdAt || a.localDate || 0).getTime();
          valB = new Date(b.createdAt || b.localDate || 0).getTime();
      }
      if (sortDir === "asc") return valA > valB ? 1 : -1;
      return valA < valB ? 1 : -1;
    });
    return list;
  }, [contacts, searchQuery, sortField, sortDir]);

  const stageCounts = stats?.byStage || {};
  const totalInPipeline = Object.values(stageCounts).reduce(
    (sum, v) => sum + (v || 0),
    0
  );

  const formatDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const daysSince = (iso) => {
    if (!iso) return null;
    return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
  };

  const handleBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!open) return null;

  const SortHeader = ({ field, children }) => (
    <th
      className="px-3 py-2.5 text-[0.65rem] font-[800] uppercase tracking-wider text-slate-400 cursor-pointer hover:text-slate-600 transition-colors select-none"
      onClick={() => toggleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown
          size={10}
          className={
            sortField === field
              ? "text-teal-800"
              : "opacity-30"
          }
        />
      </div>
    </th>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{
        backgroundColor: "rgba(15, 23, 42, 0.5)",
        backdropFilter: "blur(2px)",
      }}
      onClick={handleBackdrop}
    >
      <div className="bg-white rounded-[12px] shadow-[0_8px_30px_rgba(0,0,0,0.12)] w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50 shrink-0">
          <div>
            <h2 className="font-heading text-lg font-semibold text-slate-800 flex items-center gap-2">
              <Phone size={18} className="text-teal-800" />
              {t("marketingAllLeads", "All Leads")}
            </h2>
            <div className="flex items-center gap-3 mt-1">
              <span className="text-xs text-slate-500">
                {filteredContacts.length} {t("marketingContacts", "contacts")}
              </span>
              {totalInPipeline > 0 && (
                <span className="text-xs text-slate-500">
                  {totalInPipeline} {t("marketingInPipeline", "in pipeline")}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {Object.entries(stageCounts).map(([stage, count]) => {
              const cfg = STAGE_CONFIG[stage];
              if (!cfg || !count) return null;
              return (
                <span
                  key={stage}
                  className="px-2 py-0.5 rounded-full text-[0.6rem] font-bold"
                  style={{
                    color: cfg.color,
                    backgroundColor: cfg.bg,
                    border: `1px solid ${cfg.color}30`,
                  }}
                >
                  {t(cfg.labelKey, cfg.fallback)}: {count}
                </span>
              );
            })}
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors duration-150 cursor-pointer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-slate-100 shrink-0">
          <div className="relative">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 rounded-[10px] border border-slate-200 text-sm text-slate-800 placeholder:text-slate-300 focus:outline-none focus:border-teal-800 focus:ring-1 focus:ring-teal-800/20 transition-colors duration-150"
              placeholder={t("marketingSearchPlaceholder", "Search by phone, name, location...")}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-slim">
          {filteredContacts.length > 0 ? (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-100 sticky top-0 z-10">
                <tr>
                  <SortHeader field="createdAt">
                    {t("marketing.date", "Date")}
                  </SortHeader>
                  <th className="px-3 py-2.5 text-[0.65rem] font-[800] uppercase tracking-wider text-slate-400 text-left">
                    {t("pipeline.phoneRequired", "Phone")}
                  </th>
                  <SortHeader field="name">
                    {t("pipeline.name", "Name")}
                  </SortHeader>
                  <SortHeader field="heat">
                    {t("marketing.heat", "Heat")}
                  </SortHeader>
                  <th className="px-3 py-2.5 text-[0.65rem] font-[800] uppercase tracking-wider text-slate-400 text-left">
                    {t("marketing.location", "Location")}
                  </th>
                  <th className="px-3 py-2.5 text-[0.65rem] font-[800] uppercase tracking-wider text-slate-400 text-left">
                    {t("marketingStatus", "Status")}
                  </th>
                  <th className="px-3 py-2.5 text-[0.65rem] font-[800] uppercase tracking-wider text-slate-400 text-left">
                    {t("marketing.salesperson", "Sales")}
                  </th>
                  <SortHeader field="lastContacted">
                    {t("marketing.lastContact", "Last Contact")}
                  </SortHeader>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredContacts.map((contact) => {
                  const stage = contact.pipelineStage;
                  const stageConfig = stage ? STAGE_CONFIG[stage] : null;
                  const heatValue = contact.heat || 0;
                  const heatColor = HEAT_COLORS[heatValue] || "#94a3b8";
                  const lastDays = daysSince(contact.lastContacted);

                  return (
                    <tr
                      key={contact._id}
                      className="hover:bg-slate-50/50 transition-colors"
                    >
                      <td className="px-3 py-2 text-[0.7rem] text-slate-500 whitespace-nowrap">
                        {formatDate(contact.createdAt || contact.localDate)}
                      </td>
                      <td className="px-3 py-2 text-[0.7rem] font-[800] text-slate-800 tabular-nums">
                        {formatPhone(contact.phone)}
                      </td>
                      <td className="px-3 py-2 text-[0.7rem] text-slate-700 max-w-[120px] truncate">
                        {contact.name || (
                          <span className="italic text-slate-400">
                            {t("pipeline.noName", "No name")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {heatValue > 0 ? (
                          <span className="flex items-center gap-1">
                            <Flame size={11} style={{ color: heatColor }} />
                            <span
                              className="text-[0.65rem] font-bold"
                              style={{ color: heatColor }}
                            >
                              {heatValue}
                            </span>
                          </span>
                        ) : (
                          <span className="text-slate-300 text-[0.65rem]">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[0.65rem] text-slate-500 max-w-[100px] truncate">
                        {contact.location || "—"}
                      </td>
                      <td className="px-3 py-2">
                        {stageConfig ? (
                          <span
                            className="px-2 py-0.5 rounded-full text-[0.55rem] font-bold uppercase tracking-wider whitespace-nowrap"
                            style={{
                              color: stageConfig.color,
                              backgroundColor: stageConfig.bg,
                              border: `1px solid ${stageConfig.color}30`,
                            }}
                          >
                            {t(stageConfig.labelKey, stageConfig.fallback)}
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[0.55rem] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 border border-slate-200 whitespace-nowrap">
                            {t("marketingNew", "New")}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[0.65rem] text-slate-500 max-w-[100px] truncate">
                        {contact.salesConsultant ? (
                          <Staff id={contact.salesConsultant} />
                        ) : (
                          <span className="text-amber-500 font-semibold italic">{t("waiting", "Waiting")}...</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-[0.65rem] font-bold whitespace-nowrap">
                        {lastDays !== null ? (
                          <span
                            style={{
                              color:
                                lastDays <= 1
                                  ? "#10b981"
                                  : lastDays <= 3
                                  ? "#f59e0b"
                                  : "#ef4444",
                            }}
                          >
                            {lastDays === 0
                              ? t("pipeline.today", "Today").toLowerCase()
                              : `${lastDays}d`}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center text-slate-400">
              <Phone size={36} className="mb-3 opacity-30" />
              <p className="text-sm font-medium">
                {searchQuery
                  ? t("marketingNoSearchResults", "No contacts match your search")
                  : t("noMarketingContacts", "No contacts yet")}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}